import {
  ProductLabourCostType,
  ProductMeasurementJob,
  ProductMeasurementMaterial,
  ProductProductionRateType,
  ProductType,
} from "generated/graphql";
import * as z from "zod";
import {
  requiredNumberField,
  requiredSelectValue,
  optionalPartialAttributes,
  filterSyncArray,
  optionalProductionRateType,
  optionalExternalProductName,
} from "./sharedValidation";

export const calculateAreaMappings = {
  [ProductType.CRACK_SEAL]: (getValues, setValue) => {
    const attributes = getValues("attributes") ?? {};
    const {
      poundsPerBlock,
      poundsPerGallon,
      crackWidth,
      crackDepth,
      overbandWidth,
      overbandThickness,
    } = attributes;

    if (
      !poundsPerBlock ||
      !poundsPerGallon ||
      !crackWidth ||
      !crackDepth ||
      !overbandWidth ||
      !overbandThickness
    ) {
      return;
    }

    const value =
      ((poundsPerBlock / poundsPerGallon) * 231) /
      (crackWidth * crackDepth * 12 + overbandWidth * overbandThickness * 12);
    if (Number.isFinite(value)) {
      setValue("areaCoverage", +value.toFixed(2));
    }
  },
  [ProductType.GRADING]: (getValues, setValue) => {
    const attributes = getValues("attributes") ?? {};
    const jobMeasurement = getValues("measurementJob");
    const { poundsPerCubicYard, applicationRate } = attributes;

    if (!poundsPerCubicYard || !applicationRate) {
      return;
    }

    const cubicYardPerTon = 2000 / poundsPerCubicYard;
    const cubicFeetPerTon = cubicYardPerTon * 27;

    if (jobMeasurement === ProductMeasurementJob.SQUARE_YARD) {
      // if the job measurment is set to Square yards
      const cubicYardsPerSquareYard = ((applicationRate / 12) * 9) / 27;
      const areaCoverage = cubicYardPerTon / cubicYardsPerSquareYard;
      if (Number.isFinite(areaCoverage)) {
        setValue("areaCoverage", +areaCoverage.toFixed(2));
      }
    } else {
      // if the job measurment is set to Square feet
      const cubicYardsPerSquareFoot = (12 * 12 * applicationRate) / 1728;
      const areaCoverage = cubicFeetPerTon / cubicYardsPerSquareFoot;
      if (Number.isFinite(areaCoverage)) {
        setValue("areaCoverage", +areaCoverage.toFixed(2));
      }
    }
  },
  [ProductType.MASTIC]: (getValues, setValue) => {
    const attributes = getValues("attributes");
    const {
      poundsPerBlock = null,
      poundsPerGallon = null,
      crackWidth = null,
      crackDepth = null,
      overbandWidth = null,
      overbandThickness = null,
    } = attributes ?? {};

    if (
      !poundsPerBlock ||
      !poundsPerGallon ||
      !crackWidth ||
      !crackDepth ||
      !overbandWidth ||
      !overbandThickness
    ) {
      return;
    }

    const value =
      ((poundsPerBlock / poundsPerGallon) * 231) /
      (crackWidth * crackDepth * 12 + overbandWidth * overbandThickness * 12);
    if (Number.isFinite(value)) {
      setValue("areaCoverage", +Math.floor(value).toFixed(2));
    }
  },
};

export const requiresPlant = [
  ProductType.ASPHALT,
  ProductType.ASPHALT_CURBING,
  ProductType.MICROSURFACING,
  ProductType.SLURRY_SEAL,
  ProductType.EXCAVATE_OUT,
  ProductType.CONCRETE_FLATWORK,
  ProductType.CONCRETE_CURB_OR_GUTTER,
  ProductType.CURB_WALL,
  ProductType.CONCRETE_WATERWAY,
  ProductType.GRADING,
  ProductType.CONCRETE_FOOTING,
  ProductType.CONCRETE_WALL,
  ProductType.CONCRETE_COLUMN,
];

export const requiresTrucking = [
  ProductType.MICROSURFACING,
  ProductType.SLURRY_SEAL,
  ProductType.EXCAVATE_OUT,
  ProductType.ASPHALT,
  ProductType.ASPHALT_CURBING,
  ProductType.GRADING,
  ProductType.CONCRETE_FLATWORK,
  ProductType.CONCRETE_CURB_OR_GUTTER,
  ProductType.CONCRETE_FOOTING,
  ProductType.CONCRETE_WALL,
  ProductType.CONCRETE_COLUMN,
  ProductType.CONCRETE_WATERWAY,
];

export const concreteProducts = [
  ProductType.CONCRETE_CURB_OR_GUTTER,
  ProductType.CONCRETE_FLATWORK,
  ProductType.CONCRETE_WATERWAY,
  ProductType.CURB_WALL,
  ProductType.CONCRETE_FOOTING,
  ProductType.CONCRETE_WALL,
  ProductType.CONCRETE_COLUMN,
];

const plantProductsRequired = z.object({
  plantProducts: z.object({
    connect: z
      .array(
        z.object({
          plantId: z.string().min(1, "Plant is required"),
          id: z.string().min(1, "Plant Product is required"),
          applicationRate: z.coerce
            .number({ invalid_type_error: "Depth is required" })
            .min(0.01, "Depth is required"),
        })
      )
      .min(1, "At least one plant and plant product must be selected"),
  }),
});

const plantProductsRequiredAllowZeroDepth = z.object({
  plantProducts: z.object({
    connect: z
      .array(
        z.object({
          plantId: z.string().min(1, "Plant is required"),
          id: z.string().min(1, "Plant Product is required"),
          applicationRate: z.coerce
            .number({ invalid_type_error: "Depth is required" })
            .min(0, "Depth is required"),
        })
      )
      .min(1, "At least one plant and plant product must be selected"),
  }),
});

export const materialsNoPlantRequired = z.object({
  materialsNoPlant: z.array(
    z
      .object({
        name: z.string(),
        percentage: z.union([
          z.string().length(0),
          z.coerce
            .number({ invalid_type_error: "Percentage is required" })
            .min(0, "Percentage must be 0 or greater")
            .max(100, "Percentage cannot exceed 100"),
        ]),
        rate: z.union([
          z.string().length(0),
          z.coerce.number().min(0.01, "Rate must be greater than 0"),
        ]),
      })
      .superRefine((material, ctx) => {
        const hasRateOrPercentage = material.percentage !== "" || material.rate !== "";
        const hasNameOrPercentage = material.name.trim() !== "" || material.percentage !== "";

        if (hasRateOrPercentage && material.name.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Material name is required",
            path: ["name"],
          });
        }

        if (hasNameOrPercentage && material.rate === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Rate is required",
            path: ["rate"],
          });
        }
      })
  ),
});

export const filterFilledMaterials = (val: unknown) => {
  if (!Array.isArray(val)) {
    return undefined;
  }

  const filled = val.filter(
    (material) =>
      !!material &&
      typeof material === "object" &&
      String((material as { name?: string }).name ?? "").trim() !== ""
  );

  return filled.length > 0 ? filled : undefined;
};

export const materialsNoPlantOptional = z.preprocess(
  filterFilledMaterials,
  z.array(materialsNoPlantRequired.shape.materialsNoPlant.element).optional()
);

const productionRateTypeRequired = z.object({ productionRateType: z.boolean().optional() });

export const productTypeValidationMap = {
  [ProductType.ASPHALT]: z.object({
    attributes: z.object({}),
  }),
  [ProductType.ASPHALT_CURBING]: z.object({
    attributes: z.object({
      curbWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Width is required"),
      curbHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Height is required"),
    }),
  }),
  [ProductType.CONCRETE_CURB_OR_GUTTER]: z.object({
    attributes: z.object({
      curbWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Width is required"),
      curbHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Height is required"),
      gutterWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Width is required"),
      gutterHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Height is required"),
    }),
  }),
  [ProductType.CONCRETE_FLATWORK]: z.object({
    attributes: z.object({}),
  }),
  [ProductType.CONCRETE_WATERWAY]: z.object({
    attributes: z.object({
      curbWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Width is required"),
      curbHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Height is required"),
      gutterWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Width is required"),
      gutterHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Height is required"),
    }),
  }),
  [ProductType.CRACK_SEAL]: z.object({
    attributes: z.object({
      crackDepth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Crack Depth is required"),
      crackWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Crack Width is required"),
      overbandWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Overband Width is required"),
      overbandThickness: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Overband Thickness is required"),
      poundsPerGallon: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Pounds Per Gallon is required"),
      poundsPerBlock: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Pounds Per Block is required"),
    }),
  }),
  [ProductType.CURB_WALL]: z.object({
    attributes: z.object({
      curbWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Width is required"),
      curbHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Curb Height is required"),
      gutterWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Width is required"),
      gutterHeight: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Gutter Height is required"),
    }),
  }),
  [ProductType.EXCAVATE_OUT]: z.object({
    attributes: z.object({}),
  }),
  [ProductType.GRADING]: z.object({
    attributes: z.object({
      poundsPerCubicYard: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Pounds Per Cubic Yard is required"),
    }),
  }),
  [ProductType.MASTIC]: z.object({
    attributes: z.object({
      poundsPerGallon: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Pounds Per Gallon is required"),
      poundsPerBlock: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Pounds Per Block is required"),
      crackDepth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Crack Depth is required"),
      crackWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Crack Width is required"),
      overbandWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Overband Width is required"),
      overbandThickness: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Overband Thickness is required"),
    }),
  }),
  [ProductType.MICROSURFACING]: z.object({
    attributes: z.object({}),
  }),
  [ProductType.SEALCOAT]: z.object({
    attributes: z.object({
      applicationRate: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Application Rate is required"),
    }),
  }),
  [ProductType.SLURRY_SEAL]: z.object({
    attributes: z.object({}),
  }),
  [ProductType.STRIPING]: z.object({
    attributes: z.object({
      applicationRate: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Application Rate is required"),
      lineWidth: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Line Width is required"),
    }),
  }),
  [ProductType.CONCRETE_FOOTING]: z.object({
    attributes: z.object({
      width: z.coerce.number({ invalid_type_error: "Required" }).min(0.01, "Width is required"),
      depth: z.coerce.number({ invalid_type_error: "Required" }).min(0.01, "Depth is required"),
    }),
  }),
  [ProductType.CONCRETE_WALL]: z.object({
    attributes: z.object({
      width: z.coerce.number({ invalid_type_error: "Required" }).min(0.01, "Width is required"),
      height: z.coerce.number({ invalid_type_error: "Required" }).min(0.01, "Height is required"),
    }),
  }),
  [ProductType.CONCRETE_COLUMN]: z.object({
    attributes: z.object({
      diameter: z.coerce
        .number({ invalid_type_error: "Required" })
        .min(0.01, "Diameter is required"),
      height: z.coerce.number({ invalid_type_error: "Required" }).min(0.01, "Height is required"),
    }),
  }),
};

export const baseProductSchemaSubcontracted = z.object({
  name: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  externalProductName: z.string().optional(),
  indirectPercentage: z.number({ invalid_type_error: "Indirect Percentage is required" }).min(0),
  overheadPercentage: z.number({ invalid_type_error: "Overhead Percentage is required" }).min(0),
  markupPercentage: z.number({ invalid_type_error: "Markup Percentage is required" }).min(0),
  statementOfWork: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  subcontracted: z.boolean(),
  organizationProductTypeId: z.string().min(1, "Required"),
  measurementJob: z.nativeEnum(ProductMeasurementJob),
});
export const baseProductSchemaUnitPrice = z.object({
  name: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  externalProductName: optionalExternalProductName,
  indirectPercentage: z.number({ invalid_type_error: "Indirect Percentage is required" }).min(0),
  overheadPercentage: z.number({ invalid_type_error: "Overhead Percentage is required" }).min(0),
  markupPercentage: z.number({ invalid_type_error: "Markup Percentage is required" }).min(0),
  statementOfWork: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  unitPriceProduct: z.boolean(),
  unitPrice: requiredNumberField(
    z
      .number({
        required_error: "Please enter a Number",
        invalid_type_error: "Please enter a Number",
      })
      .min(0)
  ),
  organizationProductTypeId: z.string().min(1, "Required"),
  measurementMaterial: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z.nativeEnum(ProductMeasurementMaterial, { invalid_type_error: "Required" }).nullish()
  ),
  labourCostType: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z.nativeEnum(ProductLabourCostType).nullish()
  ),
  dailyUnitsComplete: requiredNumberField(
    z
      .number({
        required_error: "Please enter a Number",
        invalid_type_error: "Please enter a Number",
      })
      .positive()
  ),
  measurementJob: z.nativeEnum(ProductMeasurementJob),
  additionalCosts: z.preprocess(
    (val) => ({
      connect: filterSyncArray(val, (item) => Boolean(item.id), "connect"),
    }),
    z.object({
      connect: z.array(
        z.object({
          id: z.string().min(1),
          type: z.any(), //https://app.clickup.com/t/866941ydt
          name: z.string(),
        })
      ),
    })
  ),
  crews: z.preprocess(
    (val) => {
      const connect = filterSyncArray(val, (item) => Boolean(item.id), "connect");
      return connect.length > 0 ? { connect } : undefined;
    },
    z
      .object({
        connect: z.array(
          z.object({
            id: z.string().min(1, "Crew is required"),
            quantity: z
              .number({ invalid_type_error: "Quantity is required" })
              .int("Quantity must be a whole number")
              .positive("Quantity must be greater than 0"),
          })
        ),
      })
      .optional()
  ),
  productionRateType: optionalProductionRateType,
  materialsNoPlant: materialsNoPlantOptional,
});

export const getProductCreateSchemaUnitPrice = ({ productType }: { productType: ProductType }) => {
  const typeValidation = productTypeValidationMap[productType];
  let schema = baseProductSchemaUnitPrice;
  if (typeValidation) {
    schema = schema.merge(
      z.object({
        attributes: optionalPartialAttributes(typeValidation.shape.attributes),
      })
    ) as any;
  }
  // Plant products are optional for unit-priced products
  if (requiresPlant.includes(productType)) {
    schema = schema.merge(
      z.object({
        plantProducts: z.preprocess(
          (val) => {
            const connect = filterSyncArray(
              val,
              (item) => Boolean(item.plantId && item.id),
              "connect"
            );
            return connect.length > 0 ? { connect } : undefined;
          },
          z
            .object({
              connect: z.array(
                z.object({
                  plantId: z.string().min(1, "Plant is required"),
                  id: z.string().min(1, "Plant Product is required"),
                  applicationRate: z.coerce
                    .number({ invalid_type_error: "Depth is required" })
                    .min(0, "Depth is required"),
                })
              ),
            })
            .optional()
        ),
      })
    ) as any;
  }
  return schema;
};

export const baseProductSchema = z.object({
  name: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  externalProductName: z.null().or(z.string().optional()),
  measurementMaterial: z.nativeEnum(ProductMeasurementMaterial, {
    invalid_type_error: `Required`,
  }),
  costMaterial: z.null().or(z.number({ invalid_type_error: "Required" }).min(0)),
  measurementJob: z.nativeEnum(ProductMeasurementJob),
  labourCostType: z.nativeEnum(ProductLabourCostType),
  dailyUnitsComplete: requiredNumberField(
    z
      .number({
        required_error: "Please enter a Number",
        invalid_type_error: "Please enter a Number",
      })
      .positive()
  ),
  costMinimumProposal: z
    .null({ invalid_type_error: "Minimum Proposal Total Cost is required" })
    .or(z.number({ invalid_type_error: "Minimum Proposal Total Cost is required" }).min(0)),
  costMinimumProduct: z.number({ invalid_type_error: "Minimum Material Cost is required" }).min(0),
  indirectPercentage: z.number({ invalid_type_error: "Indirect Percentage is required" }).min(0),
  overheadPercentage: z.number({ invalid_type_error: "Overhead Percentage is required" }).min(0),
  markupPercentage: z.number({ invalid_type_error: "Markup Percentage is required" }).min(0),
  statementOfWork: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  subcontracted: z.boolean(),
});

export const baseSchemaMap = {
  [ProductType.ASPHALT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.ASPHALT])
    .merge(plantProductsRequiredAllowZeroDepth)
    .merge(productionRateTypeRequired),
  [ProductType.ASPHALT_CURBING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.ASPHALT_CURBING])
    .merge(plantProductsRequired)
    .merge(productionRateTypeRequired),
  [ProductType.CONCRETE_CURB_OR_GUTTER]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_CURB_OR_GUTTER])
    .merge(plantProductsRequired),
  [ProductType.CONCRETE_FLATWORK]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_FLATWORK])
    .merge(plantProductsRequired),
  [ProductType.CONCRETE_WATERWAY]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_WATERWAY])
    .merge(plantProductsRequired),
  [ProductType.CONCRETE_FOOTING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_FOOTING])
    .merge(plantProductsRequired),
  [ProductType.CONCRETE_WALL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_WALL])
    .merge(plantProductsRequired),
  [ProductType.CONCRETE_COLUMN]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CONCRETE_COLUMN])
    .merge(plantProductsRequired),
  [ProductType.CRACK_SEAL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CRACK_SEAL])
    .merge(materialsNoPlantRequired),
  [ProductType.CURB_WALL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CURB_WALL])
    .merge(plantProductsRequired),
  [ProductType.EXCAVATE_OUT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.EXCAVATE_OUT])
    .merge(plantProductsRequiredAllowZeroDepth)
    .merge(productionRateTypeRequired),
  [ProductType.GRADING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.GRADING])
    .merge(plantProductsRequiredAllowZeroDepth)
    .merge(productionRateTypeRequired),
  [ProductType.MASTIC]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.MASTIC])
    .merge(materialsNoPlantRequired),
  [ProductType.MICROSURFACING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.MICROSURFACING])
    .merge(plantProductsRequired),
  [ProductType.SEALCOAT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.SEALCOAT])
    .merge(materialsNoPlantRequired),
  [ProductType.SLURRY_SEAL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.SLURRY_SEAL])
    .merge(plantProductsRequired)
    .merge(productionRateTypeRequired),
  [ProductType.STRIPING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.STRIPING])
    .merge(materialsNoPlantRequired),
  [ProductType.GENERIC]: baseProductSchema
    .merge(
      z.object({
        areaCoverage: z
          .number({
            required_error: "Area Coverage is Required",
            invalid_type_error: "Area Coverage is Required",
          })
          .positive("Area Coverage should be positive"),
      })
    )
    .merge(materialsNoPlantRequired),
};

export const getProductCreateSchema = ({ productType }: { productType: ProductType }) =>
  baseSchemaMap[productType].merge(
    z.object({
      organizationProductTypeId: z.string().min(1, "Required"),
      equipmentPieces: z
        .object({
          connect: z.array(
            z.object({
              id: requiredSelectValue,
              quantity: requiredNumberField(
                z
                  .number({
                    required_error: "Please enter a Number",
                    invalid_type_error: "Please enter a Number",
                  })
                  .min(0, "Quantity must be 0 or greater")
              ),
              name: z.string(),
            })
          ),
        })
        .optional(),
      additionalCosts: z.object({
        connect: z.array(
          z.object({
            id: requiredSelectValue,
            type: z.any(), //https://app.clickup.com/t/866941ydt
            name: z.string(),
          })
        ),
      }),
      crews: z.object({
        connect: z
          .array(
            z.object({
              id: z.string().min(1, "Crew is required"),
              quantity: z
                .number({ invalid_type_error: "Quantity is required" })
                .int("Quantity must be a whole number")
                .positive("Quantity must be greater than 0"),
            })
          )
          .min(1, "At least one crew is required"),
      }),
    })
  );

export const defaultValues = {
  name: null,
  externalProductName: null,
  costMaterial: null,
  measurementMaterial: null,
  attributes: {
    applicationRate: null,
    curbWidth: null,
    curbHeight: null,
    gutterWidth: null,
    gutterHeight: null,
    crackWidth: null,
    crackDepth: null,
    overbandWidth: null,
    overbandThickness: null,
    poundsPerGallon: null,
    poundsPerBlock: null,
    lineWidth: null,
  },
  costMinimumProposal: null,
  costMinimumProduct: null,
  markupPercentage: null,
  indirectPercentage: null,
  overheadPercentage: null,
  statementOfWork: null,
  subcontracted: false,
  unitPriceProduct: false,
};

export const getDefaultValues = (initialDefaultValues) => ({
  ...defaultValues,
  ...initialDefaultValues,
});
